import "../../../styles/planner.css";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import Layout from "../../Layout.tsx";
import VoyagePicker from "../../FormFields/RenderVoyagePicker.tsx";
import Slideout from "../../Slideout.tsx";
import Dialog from "../../Dialog.tsx";
import MobileBottomNavigation from "../MobileBottomNavigation.tsx";
import MobileTerminalCallDetails from "../terminalCall/MobileTerminalCallDetails.tsx";
import MobileSimpleTerminalCallForm from "./MobileSimpleTerminalCallForm.tsx";
import { groupTerminalCalls } from "../../Helpers.tsx";
import { useInitialPlannerData } from "../../hooks/useInitialPlannerData.tsx";
import { getAllContainerIdsFromTerminalCalls } from "../../utils/container.tsx";
import { getSelectedContainers } from "../../../data/selectedcontainers.js";
import { selectedFilteredVessel } from "../../../selectors/filterSelector.js";

import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";
import {
  ArrowDownwardRounded as ArrowDownIcon,
  ArrowUpwardRounded as ArrowUpIcon,
  ChevronRightRounded as ChevronRightIcon,
  TaskAltRounded as CompletedIcon,
} from "@mui/icons-material";

const DEFAULT_ACCENT_COLOR = "#1976d2";
const COMPLETED_COLOR = "#159447";

const MobileVesselPlanning = () => {
  useInitialPlannerData();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [planningOpen, setPlanningOpen] = React.useState(false);
  const [selectedTerminalCall, setSelectedTerminalCall] = React.useState<any | null>(null);
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  const detailsTimer = React.useRef<number | null>(null);
  const selectedVessel = useSelector(selectedFilteredVessel);

  const {
    terminals,
    terminalCalls: { data: filteredTerminalCalls },
    containers: { data: filteredContainers },
  } = useSelector((state: any) => ({
    terminalCalls: state.selectedTerminalCalls,
    containers: state.selectedContainers,
    terminals: state.terminals,
  }));

  const safeTerminalCalls = useMemo(
    () => (Array.isArray(filteredTerminalCalls) ? filteredTerminalCalls : []),
    [filteredTerminalCalls]
  );
  const safeContainers = useMemo(
    () => (Array.isArray(filteredContainers) ? filteredContainers : []),
    [filteredContainers]
  );

  const containerIds = useMemo(
    () => getAllContainerIdsFromTerminalCalls(safeTerminalCalls),
    [safeTerminalCalls]
  );

  const terminalCallData = useMemo(
    () => groupTerminalCalls(safeTerminalCalls),
    [safeTerminalCalls]
  );

  useEffect(() => {
    const validContainerIds = containerIds.filter(
      (id) => typeof id === "string" && id.trim() !== ""
    );

    if (validContainerIds.length > 0) {
      dispatch(getSelectedContainers(validContainerIds));
    }
  }, [containerIds, dispatch]);

  useEffect(
    () => () => {
      if (detailsTimer.current !== null) window.clearTimeout(detailsTimer.current);
    },
    []
  );

  const getMatchingContainers = (containerReferences: any[] = []) => {
    const referenceIds = new Set(
      containerReferences.map((container) => container?.["@id"] ?? container)
    );

    return safeContainers.filter((container) =>
      referenceIds.has(container?.["@id"])
    );
  };

  const getTotalTeu = (containers: any[]) =>
    containers.reduce(
      (total, container) => total + Number(container?.containerType?.teu || 0),
      0
    );

  const openTerminalCallDetails = (terminalCall: any) => {
    setDetailsLoading(true);

    detailsTimer.current = window.setTimeout(() => {
      const terminalReference = terminalCall?.terminal;
      const terminalReferenceId = String(
        typeof terminalReference === "string"
          ? terminalReference
          : terminalReference?.["@id"] || terminalReference?.id || ""
      )
        .split("/")
        .filter(Boolean)
        .pop();
      const resolvedTerminal = (terminals.data || []).find((terminal: any) => {
        const terminalId = String(terminal?.["@id"] || terminal?.id || "")
          .split("/")
          .filter(Boolean)
          .pop();
        return terminalReferenceId && terminalId === terminalReferenceId;
      });

      setSelectedTerminalCall({
        ...terminalCall,
        terminal: resolvedTerminal || terminalReference || {},
      });
      setDetailsLoading(false);
      detailsTimer.current = null;
    }, 180);
  };

  const openAddPlanning = () => setPlanningOpen(true);

  return (
    <Layout>
      <Slideout />
      <Dialog />

      <Backdrop className="mobile-terminal-loading-backdrop" open={detailsLoading}>
        <Box className="mobile-terminal-loading-card" role="status" aria-live="polite">
          <CircularProgress size={38} thickness={4.5} />
          <Typography>{t("loadingTerminalCall", "Loading terminal call…")}</Typography>
        </Box>
      </Backdrop>

      <Box className="mobile-planning-page">
        <Box className="mobile-planning-toolbar">
          <VoyagePicker allowAdd={false} allowRefresh />
        </Box>

        <Box className="mobile-planning-timeline">
          {Object.entries(terminalCallData).map(([key, calls]: [string, any[]]) => {
            const firstCall = calls[0];
            const groupDate = firstCall?.planned || firstCall?.arrival;
            const dateLabel = groupDate
              ? dayjs(groupDate).format("DD MMM YYYY").toUpperCase()
              : key;

            return (
              <section className="mobile-planning-day" key={key}>
                <Typography component="h2" className="mobile-planning-date">
                  {dateLabel}
                </Typography>

                <Box className="mobile-planning-day-calls">
                  {calls.map((terminalCall: any) => {
                    const terminalCallId = terminalCall["@id"];
                    const completed =
                      terminalCall.status === "Completed" ||
                      Boolean(terminalCall.departure);
                    const accentColor = completed
                      ? COMPLETED_COLOR
                      : terminalCall.terminal?.color || DEFAULT_ACCENT_COLOR;
                    const loadingContainers = getMatchingContainers(
                      terminalCall.loadingContainers || []
                    );
                    const unloadingContainers = getMatchingContainers(
                      terminalCall.unloadingContainers || []
                    );
                    const timeStart = terminalCall.planned || terminalCall.arrival;
                    const terminalTitle =
                      terminalCall.terminal?.abbreviation ||
                      terminalCall.terminal?.name ||
                      t("terminal", "Terminal");
                    const location = [
                      terminalCall.terminal?.name !== terminalTitle
                        ? terminalCall.terminal?.name
                        : null,
                      terminalCall.terminal?.city,
                    ]
                      .filter(Boolean)
                      .join(" · ");

                    return (
                      <Box
                        component="button"
                        type="button"
                        className="mobile-planning-call"
                        key={terminalCallId}
                        onClick={() => openTerminalCallDetails(terminalCall)}
                        style={{ "--call-accent": accentColor } as React.CSSProperties}
                        aria-label={`${terminalTitle}, ${dayjs(timeStart).format("HH:mm")}`}
                      >
                        <span className="mobile-planning-timeline-marker">
                          {completed && <CompletedIcon />}
                        </span>

                        <span className="mobile-planning-call-content">
                          <span className="mobile-planning-call-main">
                            <span className="mobile-planning-time">
                              {dayjs(timeStart).format("HH:mm")}
                              {completed && terminalCall.departure
                                ? ` – ${dayjs(terminalCall.departure).format("HH:mm")}`
                                : ""}
                            </span>
                            <span className="mobile-planning-terminal-name">
                              {terminalTitle}
                            </span>
                            {location && (
                              <span className="mobile-planning-location">
                                {location}
                              </span>
                            )}
                          </span>

                          <span className="mobile-planning-call-side">
                            <span
                              className={`mobile-planning-status${
                                completed ? " mobile-planning-status--completed" : ""
                              }`}
                            >
                              {completed
                                ? t("completed", "Completed")
                                : t("planned", "Planned")}
                            </span>

                            <span className="mobile-planning-cargo-row">
                              <ArrowUpIcon />
                              <span>{t("unload", "Unload")}</span>
                              <strong>{unloadingContainers.length}</strong>
                              <b>{getTotalTeu(unloadingContainers)} TEU</b>
                            </span>
                            <span className="mobile-planning-cargo-row mobile-planning-cargo-row--load">
                              <ArrowDownIcon />
                              <span>{t("load", "Load")}</span>
                              <strong>{loadingContainers.length}</strong>
                              <b>{getTotalTeu(loadingContainers)} TEU</b>
                            </span>
                          </span>

                          <ChevronRightIcon className="mobile-planning-chevron" />
                        </span>
                      </Box>
                    );
                  })}
                </Box>
              </section>
            );
          })}
        </Box>
      </Box>

      <MobileBottomNavigation active="timeline" onAdd={openAddPlanning} />

      <MobileSimpleTerminalCallForm
        open={planningOpen}
        onClose={() => setPlanningOpen(false)}
        terminals={terminals.data || []}
        vessel={selectedVessel}
      />

      <MobileTerminalCallDetails
        open={Boolean(selectedTerminalCall)}
        terminalCall={selectedTerminalCall}
        terminalCalls={safeTerminalCalls}
        terminals={terminals.data || []}
        containers={safeContainers}
        vessel={selectedVessel}
        onClose={() => setSelectedTerminalCall(null)}
        onAdd={openAddPlanning}
      />
    </Layout>
  );
};

export default MobileVesselPlanning;
